import React, { useEffect, useRef } from 'react';
import { Skull, X, Star } from 'lucide-react';

/**
 * 🏆 VictoryModal — celebrates when the PLAYER eliminates an AI nation.
 *
 * Shows:
 *   - Triumphant happy sprite of the player's leader
 *   - Defeated leader sprite stamped with a giant ☠️ skull
 *   - 3-star progress (filled stars = total eliminations this match)
 *   - Country names and a fanfare voiceover
 */

// Map country code → file basename used in /assets/leaders/
const LEADER_SPRITE_BASE = {
  USA: 'usa',
  RUS: 'russia',
  CHN: 'china',
  EU: 'europe',
  NK: 'nkorea',
};

const getHappySprite = (code) => {
  const base = LEADER_SPRITE_BASE[code] || (code || '').toLowerCase();
  return `/assets/leaders/${base}-happy.png`;
};

const getLeaderSprite = (code) => {
  const base = LEADER_SPRITE_BASE[code] || (code || '').toLowerCase();
  return `/assets/leaders/${base}.png`;
};

export default function VictoryModal({ defeatedCountry, playerCountry, stats = {}, onClose }) {
  const audioRef = useRef(null);

  useEffect(() => {
    if (!defeatedCountry) return;

    // 🔈 Play victory fanfare + thanks voiceover. We use the existing
    // thanks-for-playing.mp3 since it has a triumphant tone. Released
    // when modal closes.
    const audio = new Audio('/assets/audio/thanks-for-playing.mp3');
    audio.volume = 0.75;
    audio.play().catch((e) => console.log('Victory fanfare failed:', e));
    audioRef.current = audio;

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [defeatedCountry]);

  if (!defeatedCountry) return null;

  const starsEarned = Math.max(0, Math.min(3, playerCountry?.stars || 1));
  const starsToWin = 3;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black bg-opacity-95 backdrop-blur-md p-4 animate-fadeIn"
      onClick={onClose}
      data-testid="victory-modal"
    >
      <div
        className="bg-gradient-to-br from-amber-950 via-gray-900 to-red-950 border-4 border-amber-500 rounded-lg max-w-2xl w-full shadow-2xl relative animate-scaleIn"
        style={{ boxShadow: '0 0 60px rgba(245, 158, 11, 0.5)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-50 bg-red-600 hover:bg-red-500 text-white rounded-full p-1.5 transition-all hover:scale-110"
          data-testid="victory-modal-close-btn"
        >
          <X className="w-4 h-4 md:w-5 md:h-5" />
        </button>

        <div className="p-4 md:p-8">
          {/* Header */}
          <div className="text-center mb-4 md:mb-6">
            <h2 className="text-3xl md:text-5xl font-bold text-amber-400 mb-1 md:mb-2 animate-pulse tracking-wider" style={{ fontFamily: "'Orbitron', sans-serif" }}>
              🎖️ ENEMY ELIMINATED 🎖️
            </h2>
            <div className="text-sm md:text-base text-amber-200">
              {playerCountry?.name || 'YOU'} have crushed {defeatedCountry.countryName}!
            </div>
          </div>

          {/* Leaders face-off: HAPPY player vs SKULL-stamped defeated */}
          <div className="flex items-end justify-center gap-3 md:gap-6 mb-4 md:mb-6">
            {/* Player (Happy) */}
            <div className="flex flex-col items-center">
              <img
                src={getHappySprite(playerCountry?.code)}
                alt={`${playerCountry?.name} leader`}
                className="w-28 h-28 md:w-44 md:h-44 object-contain"
                style={{ filter: 'drop-shadow(0 0 25px rgba(251, 191, 36, 0.9))' }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = getLeaderSprite(playerCountry?.code);
                }}
              />
              <div className="mt-1 text-amber-300 font-bold text-xs md:text-sm uppercase">
                {playerCountry?.name || 'Commander'}
              </div>
              <div className="text-[10px] md:text-xs text-amber-400/80 font-mono">VICTORIOUS</div>
            </div>

            {/* VS swords */}
            <div className="text-2xl md:text-4xl text-amber-500 mb-8 md:mb-12">⚔️</div>

            {/* Defeated (Skull-stamped) */}
            <div className="flex flex-col items-center relative">
              <div className="relative">
                <img
                  src={getLeaderSprite(defeatedCountry.countryCode)}
                  alt={`${defeatedCountry.countryName} leader`}
                  className="w-28 h-28 md:w-44 md:h-44 object-contain"
                  style={{ filter: 'grayscale(0.9) brightness(0.55) drop-shadow(0 0 15px rgba(239, 68, 68, 0.6))' }}
                />
                {/* Giant SKULL overlay */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <Skull
                    className="w-20 h-20 md:w-32 md:h-32 text-red-500 animate-pulse"
                    style={{ filter: 'drop-shadow(0 0 18px rgba(239, 68, 68, 0.9))' }}
                  />
                </div>
              </div>
              <div className="mt-1 text-red-400 font-bold text-xs md:text-sm uppercase">
                {defeatedCountry.countryName}
              </div>
              <div className="text-[10px] md:text-xs text-red-500/80 font-mono">DEFEATED</div>
            </div>
          </div>

          {/* Star progress bar */}
          <div className="bg-black/50 border-2 border-amber-500/60 rounded-lg p-3 md:p-4 mb-4 md:mb-6">
            <div className="text-center text-amber-300 font-bold text-xs md:text-sm uppercase tracking-widest mb-2">
              VICTORY STARS · {starsEarned} of {starsToWin}
            </div>
            <div className="flex items-center justify-center gap-3 md:gap-6" data-testid="victory-stars">
              {[1, 2, 3].map((slot) => {
                const earned = slot <= starsEarned;
                return (
                  <Star
                    key={slot}
                    className={`w-10 h-10 md:w-14 md:h-14 transition-all ${earned ? 'animate-pulse' : ''}`}
                    fill={earned ? '#fbbf24' : 'transparent'}
                    stroke={earned ? '#fbbf24' : '#475569'}
                    strokeWidth={2}
                    style={
                      earned
                        ? { filter: 'drop-shadow(0 0 14px rgba(251, 191, 36, 0.9))' }
                        : undefined
                    }
                    data-testid={`victory-star-${slot}-${earned ? 'earned' : 'empty'}`}
                  />
                );
              })}
            </div>
            {starsEarned < starsToWin && (
              <div className="text-center text-[10px] md:text-xs text-amber-200/70 mt-2 font-mono">
                {starsToWin - starsEarned} more elimination{starsToWin - starsEarned !== 1 ? 's' : ''} to total domination
              </div>
            )}
            {starsEarned >= starsToWin && (
              <div className="text-center text-sm md:text-base text-amber-300 mt-2 font-bold animate-pulse">
                🏆 TOTAL DOMINATION ACHIEVED 🏆
              </div>
            )}
          </div>

          {/* Continue Button */}
          <button
            onClick={onClose}
            className="w-full bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-white font-bold py-3 md:py-4 px-6 rounded-lg text-base md:text-xl transition-all shadow-lg hover:shadow-xl"
            data-testid="victory-modal-continue-btn"
          >
            ⚔️ CONTINUE DOMINATION
          </button>
        </div>
      </div>
    </div>
  );
}
